import { createFileRoute } from '@tanstack/react-router';
import { Check, Copy, Webhook } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export const Route = createFileRoute('/_authed/webhook-verification')({
  component: WebhookVerification,
});

const TOLERANCE_SECONDS = 300;
const HEADER = 'Hub-Signature';
const FORMAT = 't=<unix_ts>,v1=<hmac_hex>';

function WebhookVerification() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <header>
        <div className="flex items-center gap-2">
          <Webhook className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('webhook_verification.title')}
          </h1>
        </div>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          {t('webhook_verification.description')}
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('webhook_verification.algorithm_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <ol className="list-decimal space-y-2 pl-6">
            <li>
              Read the <code className="font-mono text-xs">{HEADER}</code> request header. Format
              is <code className="font-mono text-xs">{FORMAT}</code>.
            </li>
            <li>
              Parse the <code>t</code> and <code>v1</code> components.
            </li>
            <li>
              Reject if <code>|now − t| &gt; {TOLERANCE_SECONDS}s</code> (replay window).
            </li>
            <li>
              Compute{' '}
              <code className="font-mono text-xs">
                HMAC-SHA256(secret, f&quot;{'{'}t{'}'}.&quot; + raw_body)
              </code>
              , hex-encoded.
            </li>
            <li>
              Compare to <code>v1</code> with a constant-time comparison. Reject on mismatch.
            </li>
            <li>
              Use the <strong>raw</strong> request body bytes — not the parsed JSON. Any framework
              that re-serializes the body before your handler sees it will break verification.
            </li>
          </ol>
          <p className="text-xs text-muted-foreground">
            The webhook secret is the value you saved when creating the endpoint at{' '}
            <code>/admin/merchants/&lt;id&gt;/webhooks</code>. If you lost it, rotate it — we
            cannot recover it (we only store the encrypted form).
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('webhook_verification.snippets_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="python">
            <TabsList>
              <TabsTrigger value="python">Python</TabsTrigger>
              <TabsTrigger value="node">Node / TS</TabsTrigger>
              <TabsTrigger value="go">Go</TabsTrigger>
              <TabsTrigger value="php">PHP</TabsTrigger>
            </TabsList>
            {Object.entries(SNIPPETS).map(([k, snip]) => (
              <TabsContent key={k} value={k}>
                <CodeBlock code={snip} />
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

function CodeBlock({ code }: { code: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      toast.success(t('common.copied') as string);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        className="absolute right-2 top-2 z-10"
        onClick={onCopy}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
      <pre className="overflow-x-auto rounded-md border border-border bg-muted/30 p-4 text-xs">
        <code>{code}</code>
      </pre>
    </div>
  );
}

const SNIPPETS: Record<string, string> = {
  python: `import hmac, hashlib, time

def verify(secret: bytes, body: bytes, header: str, *, tolerance: int = 300) -> bool:
    """Return True iff the Hub-Signature header is valid for body.

    secret: the per-endpoint webhook secret you saved at creation
    body:   the *raw* request body bytes
    header: value of the "Hub-Signature" request header
    """
    parts = dict(p.split("=", 1) for p in header.split(",") if "=" in p)
    try:
        t = int(parts["t"])
        v1 = parts["v1"]
    except (KeyError, ValueError):
        return False
    if abs(int(time.time()) - t) > tolerance:
        return False
    expected = hmac.new(secret, f"{t}.".encode() + body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, v1)`,

  node: `import { createHmac, timingSafeEqual } from "node:crypto";

export function verify(
  secret: Buffer,
  body: Buffer,
  header: string,
  toleranceSeconds = 300,
): boolean {
  const parts = Object.fromEntries(
    header.split(",").filter((p) => p.includes("=")).map((p) => p.split("=", 2)),
  );
  const t = Number(parts.t);
  const v1 = String(parts.v1 ?? "");
  if (!Number.isFinite(t)) return false;
  if (Math.abs(Math.floor(Date.now() / 1000) - t) > toleranceSeconds) return false;

  const mac = createHmac("sha256", secret);
  mac.update(\`\${t}.\`);
  mac.update(body);
  const expected = mac.digest("hex");
  if (expected.length !== v1.length) return false;
  return timingSafeEqual(Buffer.from(expected, "hex"), Buffer.from(v1, "hex"));
}`,

  go: `package webhook

import (
    "crypto/hmac"
    "crypto/sha256"
    "encoding/hex"
    "fmt"
    "strconv"
    "strings"
    "time"
)

func Verify(secret, body []byte, header string, tolerance time.Duration) bool {
    var t int64
    var v1 string
    for _, part := range strings.Split(header, ",") {
        kv := strings.SplitN(part, "=", 2)
        if len(kv) != 2 {
            continue
        }
        switch kv[0] {
        case "t":
            n, err := strconv.ParseInt(kv[1], 10, 64)
            if err != nil {
                return false
            }
            t = n
        case "v1":
            v1 = kv[1]
        }
    }
    if t == 0 || v1 == "" {
        return false
    }
    drift := time.Since(time.Unix(t, 0))
    if drift < 0 {
        drift = -drift
    }
    if drift > tolerance {
        return false
    }
    mac := hmac.New(sha256.New, secret)
    fmt.Fprintf(mac, "%d.", t)
    mac.Write(body)
    expected := hex.EncodeToString(mac.Sum(nil))
    return hmac.Equal([]byte(expected), []byte(v1))
}`,

  php: `<?php
function payhub_verify(string $secret, string $body, string $header, int $tolerance = 300): bool {
    $parts = [];
    foreach (explode(',', $header) as $p) {
        if (strpos($p, '=') !== false) {
            [$k, $v] = explode('=', $p, 2);
            $parts[$k] = $v;
        }
    }
    if (!isset($parts['t'], $parts['v1'])) {
        return false;
    }
    $t = (int) $parts['t'];
    if (abs(time() - $t) > $tolerance) {
        return false;
    }
    $expected = hash_hmac('sha256', $t . '.' . $body, $secret);
    return hash_equals($expected, $parts['v1']);
}`,
};
